/**
 * The money moment on a Work card: one gesture, one answer, in place.
 *
 * Alan on the round Confirm button (2026-09-20): "passt irgendwie nicht rein …
 * ein Swipe to Confirm oder so was". Alan on the swipe (2026-09-21): "looks too
 * far to slide" on the desktop. Both are the same complaint about the same
 * thing - the gesture has to match the hand.
 *
 *   phone / tablet   slide the knob to the end of a thumb-sized track
 *   mouse / trackpad press and hold the pill; it goes dark, gives a little, and
 *                    a soft orange glow slides in until it floods, in 0.8s
 *   keyboard         Enter or Space on the control (one press, never held)
 *
 * The two draw DIFFERENTLY on purpose. A knob sitting in a track is a promise
 * that it can be dragged; on the desktop there is nothing to drag, so there is
 * no knob - the pill itself is the button and its label says "Hold to confirm".
 * A control that shows one affordance and wants another is how you get a
 * customer poking at a payment.
 *
 * What will be charged is in the label ("Hold to confirm · €220") and how
 * ("Card on file · …") sits under it - the same disclosure the PaySheet gives,
 * one gesture instead of two taps.
 *
 * @module web/screens/app/work/SwipeToConfirm
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type KeyboardEvent } from "react";
import { HOLD_MS, clampKnob, gestureFor, swipeConfirms } from "./swipe.ts";

export type SwipeResult = { ok: true; message: string } | { ok: false; message: string };

const KNOB = 44;
const PAD = 3;

export function SwipeToConfirm({ id, action, amount, disabled = false, onConfirm }: {
  /** Put on the control that takes the gesture (the pill, or the knob). */
  id?: string;
  /** What it does, without the verb: "Confirm" or "Confirm all". */
  action: string;
  /** The formatted amount, e.g. "€220". */
  amount: string;
  disabled?: boolean;
  onConfirm: () => Promise<SwipeResult>;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [x, setX] = useState(0);
  const [state, setState] = useState<"idle" | "drag" | "busy" | "done" | "error">("idle");
  const [msg, setMsg] = useState<string | null>(null);
  const [nudge, setNudge] = useState(0);
  const [gesture] = useState(gestureFor);
  const start = useRef<{ px: number; x0: number; moved: boolean } | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A hold interrupted by an unmount must not fire afterwards.
  useEffect(() => () => { if (holdTimer.current) clearTimeout(holdTimer.current); }, []);

  const max = () => Math.max(0, (track.current?.clientWidth ?? 0) - KNOB - PAD * 2);
  const locked = disabled || state === "busy" || state === "done";
  const verb = `${gesture === "hold" ? "Hold to" : "Swipe to"} ${action.toLowerCase()}`;
  const label = `${verb} · ${amount}`;

  async function run(): Promise<void> {
    buzz(8);
    setState("busy");
    setMsg(null);
    setX(max());
    const r = await onConfirm();
    if (r.ok) {
      buzz([10, 60, 14]);
      setState("done");
      setMsg(r.message);
    } else {
      buzz([28, 40, 28]);
      setState("error");
      setMsg(r.message);
      setX(0);
    }
  }

  /**
   * CAPTURES the pointer. Without it the button gets a `pointerleave` the moment
   * the cursor drifts a pixel off the pill and the hold dies mid-way - the same
   * "my gesture almost worked and nothing happened" the long slide caused. With
   * capture only lifting the button (or the browser cancelling the pointer) ends
   * it; the held second is what guards against an accident, not a steady hand.
   */
  function beginHold(e: ReactPointerEvent<HTMLButtonElement>): void {
    if (locked || holdTimer.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setMsg(null);
    setState("drag");
    holdTimer.current = setTimeout(() => { holdTimer.current = null; void run(); }, HOLD_MS);
  }
  /**
   * Let go early: the fill drains back and the pill says what it wants. Alan
   * (2026-09-26) found the old hold "unintuitive" - a click that did nothing and
   * said nothing. Now the press answers at once (dark, filling) and an early
   * release says "keep holding" instead of silently resetting.
   */
  function cancelHold(): void {
    if (!holdTimer.current) return;
    clearTimeout(holdTimer.current);
    holdTimer.current = null;
    setState("idle");
    setMsg("Keep holding - it takes a second");
  }
  /**
   * Keyboard confirms on a single Enter or Space, on BOTH gestures - it is never
   * held. Tabbing here is already the deliberate act the pointer gestures have
   * to manufacture, and making payment depend on holding a key down would lock
   * out exactly the people a keyboard path exists for.
   */
  function onKeyDown(e: KeyboardEvent): void {
    if (locked || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    if (!e.repeat) void run();
  }

  // Apple Pay says one word at the end; the server's sentence goes underneath,
  // where it can wrap instead of running into the mark.
  const shown = state === "busy" ? "Confirming…" : "Done";
  const body = gesture === "hold"
    ? (
      <>
        {/* Alan 2026-09-21: "u cant read hold to confirm fully" - then the
            fill was a faint wash under ink text. Since 2026-09-26 the pill goes
            dark and the brand GRADIENT (not flat #FF6B35, which fails AA under
            white) refills it; the label is white on both, so every glyph stays
            readable at every millisecond. See tabs6b.css "Hold to confirm". */}
        <div className="swipe-fill" />
        <span className="swipe-label" key={labelKey(state)}>
          {state === "idle" || state === "drag" || state === "error"
            ? <><span>{verb}</span><b className="swipe-amount">{amount}</b></>
            : <>
                {state === "done" ? <SuccessMark ring /> : state === "busy" ? <ProcessingRing /> : null}
                <span>{shown}</span>
              </>}
        </span>
      </>
    )
    : (
      <>
        <div className="swipe-fill" style={{ width: x + KNOB + PAD * 2 }} />
        <span className="swipe-label" key={labelKey(state)} style={{ opacity: labelFade(state, x, max()) }}>
          {state === "idle" || state === "drag" || state === "error"
            ? <><span>{verb}</span><b className="swipe-amount">{amount}</b></>
            : shown}
        </span>
      </>
    );

  if (gesture === "hold") {
    return (
      // --hold: the one duration the fill, the give and the timer share (tabs6b.css).
      <div className="swipe-wrap" data-state={state} data-gesture="hold" style={{ "--hold": `${HOLD_MS}ms` } as React.CSSProperties}>
        <button
          type="button"
          className="swipe swipe-hold"
          id={id}
          data-action="confirm"
          ref={track as unknown as React.Ref<HTMLButtonElement>}
          aria-label={label}
          disabled={disabled}
          onPointerDown={beginHold}
          onPointerUp={cancelHold}
          onPointerCancel={cancelHold}
          onKeyDown={onKeyDown}
        >
          {body}
        </button>
        {state === "done" && msg && <p className="swipe-note ok" role="status">{msg}</p>}
        {state === "error" && msg && <p className="swipe-note err" role="alert">{msg}</p>}
        {state === "idle" && msg && <p className="swipe-note" role="status">{msg}</p>}
      </div>
    );
  }

  function onDown(e: ReactPointerEvent<HTMLButtonElement>): void {
    if (locked) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { px: e.clientX, x0: x, moved: false };
    setState("drag");
  }
  function onMove(e: ReactPointerEvent<HTMLButtonElement>): void {
    const s = start.current;
    if (!s) return;
    if (Math.abs(e.clientX - s.px) > 3) s.moved = true;
    setX(clampKnob(s.x0 + e.clientX - s.px, max()));
  }
  function onUp(): void {
    const s = start.current;
    if (!s) return;
    start.current = null;
    if (swipeConfirms(x, max())) { void run(); return; }
    if (!s.moved) setNudge(n => n + 1);
    setState("idle");
    setX(0);
  }

  return (
    <div className="swipe-wrap" data-state={state} data-gesture="swipe">
      <div className={`swipe${nudge ? " nudge" : ""}`} ref={track} key={nudge}>
        {body}
        <button
          type="button"
          className="swipe-knob"
          id={id}
          data-action="confirm"
          aria-label={label}
          disabled={disabled}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onKeyDown={onKeyDown}
          style={{ transform: `translateX(${x}px)` }}
        >
          {state === "done" ? <SuccessMark /> : state === "busy" ? <ProcessingRing /> : <ArrowIcon />}
        </button>
      </div>
      {state === "done" && msg && <p className="swipe-note ok" role="status">{msg}</p>}
      {state === "error" && msg && <p className="swipe-note err" role="alert">{msg}</p>}
    </div>
  );
}

/** The knob eats its own label as it travels; nothing to fade when it cannot travel. */
function labelFade(state: string, x: number, max: number): number {
  if (state === "done" || state === "busy") return 1;
  return Math.max(0, 1 - (max > 0 ? x / max : 0) * 1.6);
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" /><path d="M13 6l6 6-6 6" />
    </svg>
  );
}
/**
 * The Apple Pay ending (Alan 2026-10-01: "cooler machen mit so apple pay
 * feeling"): the processing ring closes into a circle, then the check draws
 * itself. Both strokes use pathLength=1 so the CSS draw is one dashoffset 1→0,
 * whatever the geometry. `ring` off inside the swipe knob - the knob already is
 * the circle.
 */
function SuccessMark({ ring = false }: { ring?: boolean }) {
  return (
    <svg className="swipe-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      {ring && <circle className="swipe-success-ring" cx="12" cy="12" r="10.5" pathLength={1} transform="rotate(-90 12 12)" />}
      <path className="swipe-success-check" d={ring ? "M7.5 12.4l3 3 6-6.4" : "M6 12.5l4 4 8-8.6"} pathLength={1} />
    </svg>
  );
}
/** A thin quarter arc spinning fast - fast spin reads as fast work. */
function ProcessingRing() {
  return (
    <svg className="swipe-processing" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10.5" opacity=".25" />
      <circle cx="12" cy="12" r="10.5" pathLength={1} strokeDasharray=".28 1" />
    </svg>
  );
}
/** idle and drag share a label; every other state brings its own words in. */
function labelKey(state: string): string {
  return state === "drag" ? "idle" : state;
}
/** A tick on Android; iOS Safari has no vibrate and simply ignores it. */
function buzz(pattern: number | number[]): void {
  try { globalThis.navigator?.vibrate?.(pattern); } catch { /* no haptics here */ }
}
